import { Item, Root, Trigger } from '@radix-ui/react-accordion';
import { FC, useId, useState } from 'react';

import CollapsiblePanel from 'molecules/CollapsiblePanel';
import Icon from 'molecules/Icon';

import TableRow from 'components/Tables/PricingV2/components/TableRow';
import { PricingTableConfigProps } from 'components/Tables/PricingV2/config';
import { useScrollToAccordionItem } from 'components/Tables/PricingV2/hooks/useScrollToAccordionItem';

import { hasArrayValues } from 'utils/array/arrays';

interface TableAccordionProps extends Pick<
  PricingTableConfigProps,
  'tableSections' | 'tierOneTitle' | 'tierTwoTitle' | 'tierThreeTitle'
> {}

const TableAccordion: FC<TableAccordionProps> = ({ tableSections, tierOneTitle, tierTwoTitle, tierThreeTitle }) => {
  const [openValue, setOpenValue] = useState<string>(tableSections?.[0]?.id || '');
  const idPrefix = useId();
  const refs = useScrollToAccordionItem(openValue);

  return (
    hasArrayValues(tableSections) && (
      <Root type="single" collapsible value={openValue} onValueChange={setOpenValue} defaultValue={tableSections[0].id}>
        <div className="flex flex-col gap-6">
          {tableSections.map(section => (
            <Item
              key={section.id}
              value={section.id}
              className="rounded-lg border border-solid border-transparent border-b-white/20 bg-white/10 data-[state=open]:border-primary-300/[.32]"
            >
              <div
                ref={el => {
                  refs.current[section.id] = el;
                }}
              />

              <Trigger
                id={`${idPrefix}-trigger-${section.id}`}
                aria-controls={`${idPrefix}-panel-${section.id}`}
                className="group flex w-full justify-between px-6 py-5 text-primary-400"
              >
                <span className="text-xl font-bold text-white">{section.label}</span>
                <Icon
                  icon="chevron-down"
                  className="stroke-2 transition-transform group-data-[state=open]:rotate-180"
                />
              </Trigger>

              <CollapsiblePanel
                id={`${idPrefix}-panel-${section.id}`}
                labelledBy={`${idPrefix}-trigger-${section.id}`}
                isOpen={openValue === section.id}
              >
                {/*
                  A real <table> so HTML-to-Markdown converters and assistive tech get rows, row headers and
                  plan columns. The caption and column headers are visually hidden (the plan names are shown in
                  the heading above), and table/tbody are blocks with flex rows so the layout is unchanged.
                */}
                {hasArrayValues(section.rows) && (
                  <table className="block w-full">
                    <caption className="sr-only">{section.label}</caption>
                    <thead className="sr-only">
                      <tr>
                        <th scope="col">Feature</th>
                        <th scope="col">{tierOneTitle}</th>
                        <th scope="col">{tierTwoTitle}</th>
                        <th scope="col">{tierThreeTitle}</th>
                      </tr>
                    </thead>
                    <tbody className="block">
                      {section.rows.map(row => (
                        <TableRow
                          key={row.rowHeading}
                          {...row}
                          tierOneTitle={tierOneTitle}
                          tierTwoTitle={tierTwoTitle}
                          tierThreeTitle={tierThreeTitle}
                        />
                      ))}
                    </tbody>
                  </table>
                )}
              </CollapsiblePanel>
            </Item>
          ))}
        </div>
      </Root>
    )
  );
};

export default TableAccordion;
